import React from 'react';
import { format } from 'date-fns';
import { CalendarDays, X } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';
import {
  formatDateRangeLabel,
  getEffectiveMinimumDateKey,
  isEndBeforeStart,
} from './datePickerUtils';

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}/;

const getDateKey = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toDateKey(value);
  }
  const candidate = String(value);
  if (DATE_KEY_PATTERN.test(candidate)) return candidate.slice(0, 10);
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime()) ? '' : toDateKey(parsed);
};

const fromDateKey = (value) => {
  const key = getDateKey(value);
  if (!key) return undefined;
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMinimumDateKey = (minDate, allowPastDates) => (
  getEffectiveMinimumDateKey(minDate, new Date(), allowPastDates)
);

const clampToMinimumKey = (value, minimumDateKey) => {
  const valueKey = getDateKey(value);
  if (!valueKey) return '';
  return minimumDateKey && valueKey < minimumDateKey
    ? minimumDateKey
    : valueKey;
};

const addDaysToKey = (value, days) => {
  const date = fromDateKey(value);
  if (!date) return '';
  date.setDate(date.getDate() + days);
  return toDateKey(date);
};

const formatDateLabel = (value) => {
  const date = fromDateKey(value);
  return date ? format(date, 'MMM d, yyyy') : '';
};

const formatEventWindow = (eventStartDate, eventEndDate) => {
  const startKey = getDateKey(eventStartDate);
  const endKey = getDateKey(eventEndDate) || startKey;
  if (!startKey) return '';
  if (startKey === endKey) return formatDateLabel(startKey);
  return `${formatDateLabel(startKey)} to ${formatDateLabel(endKey)}`;
};

const isBeforeKey = (left, right) => Boolean(left && right && left < right);
const isAfterKey = (left, right) => Boolean(left && right && left > right);

const getEventModifiers = (eventStartDate, eventEndDate) => {
  const start = fromDateKey(eventStartDate);
  if (!start) return {};
  const end = fromDateKey(eventEndDate) || start;
  return {
    eventRange: { from: start, to: end },
    eventStart: start,
    eventEnd: end,
  };
};

const eventModifierClassNames = {
  eventRange: 'bg-emerald-50 text-emerald-900',
  eventStart: 'ring-1 ring-inset ring-emerald-500',
  eventEnd: 'ring-1 ring-inset ring-emerald-500',
};

function EventContextHint({ eventStartDate, eventEndDate, selectedStart, selectedEnd, helperText }) {
  const eventStartKey = getDateKey(eventStartDate);
  if (!eventStartKey) return helperText ? <p className="text-xs text-muted-foreground">{helperText}</p> : null;

  const eventEndKey = getDateKey(eventEndDate) || eventStartKey;
  const selectedStartKey = getDateKey(selectedStart);
  const selectedEndKey = getDateKey(selectedEnd) || selectedStartKey;
  const eventWindow = formatEventWindow(eventStartKey, eventEndKey);
  const selectedWindow = selectedStartKey && selectedEndKey
    ? (selectedStartKey === selectedEndKey
      ? formatDateLabel(selectedStartKey)
      : `${formatDateLabel(selectedStartKey)} to ${formatDateLabel(selectedEndKey)}`)
    : '';
  const outsideEventWindow = selectedStartKey && selectedEndKey && (
    isBeforeKey(selectedStartKey, eventStartKey) || isAfterKey(selectedEndKey, eventEndKey)
  );

  return (
    <p className={cn('text-xs', outsideEventWindow ? 'text-amber-700' : 'text-muted-foreground')}>
      {outsideEventWindow
        ? `Event runs ${eventWindow}; selected ${selectedWindow}.`
        : helperText || `Event window: ${eventWindow}.`}
    </p>
  );
}

export function EventAwareDatePicker({
  id,
  value,
  onChange,
  eventStartDate,
  eventEndDate,
  label,
  placeholder = 'Pick date',
  required = false,
  disabled = false,
  defaultToEventStart = true,
  defaultDate,
  minDate,
  maxDate,
  allowPastDates = false,
  helperText,
  validationMessage,
  className,
  triggerClassName,
  'data-testid': dataTestId,
}) {
  const [open, setOpen] = React.useState(false);
  const selectedDate = fromDateKey(value);
  const minimumDateKey = getMinimumDateKey(minDate, allowPastDates);
  const defaultDateKey = clampToMinimumKey(
    getDateKey(defaultDate) || getDateKey(eventStartDate),
    minimumDateKey
  );
  const minimumDate = fromDateKey(minimumDateKey);
  const maximumDate = fromDateKey(maxDate);
  const disabledDates = [
    minimumDate ? { before: minimumDate } : null,
    maximumDate ? { after: maximumDate } : null,
  ].filter(Boolean);
  const preferredMonth = fromDateKey(clampToMinimumKey(
    getDateKey(value) || defaultDateKey || minimumDateKey,
    minimumDateKey
  )) || minimumDate;
  const [month, setMonth] = React.useState(preferredMonth);
  const initialDefaultAppliedRef = React.useRef(false);

  React.useEffect(() => {
    if (!defaultToEventStart || !defaultDateKey || value) return;
    if (initialDefaultAppliedRef.current) return;
    initialDefaultAppliedRef.current = true;
    onChange?.(defaultDateKey);
  }, [defaultDateKey, defaultToEventStart, onChange, value]);

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      const nextValue = getDateKey(value) || (defaultToEventStart ? defaultDateKey : '');
      if (!value && nextValue) onChange?.(nextValue);
      setMonth(fromDateKey(clampToMinimumKey(
        nextValue || defaultDateKey || minimumDateKey,
        minimumDateKey
      )) || minimumDate);
    }
    setOpen(nextOpen);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && <label htmlFor={id} className="text-sm font-medium">{label}{required ? ' *' : ''}</label>}
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              disabled={disabled}
              aria-required={required}
              aria-invalid={Boolean(validationMessage)}
              className={cn('h-9 w-full justify-start rounded-md px-3 text-left font-normal', !value && 'text-muted-foreground', triggerClassName)}
              data-testid={dataTestId}
            >
              <CalendarDays className="mr-2 h-4 w-4" />
              {value ? formatDateLabel(value) : placeholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" collisionPadding={12} className="z-[90] w-[19rem] max-w-[calc(100vw-1.5rem)] p-0">
            <Calendar
              allowPastDates={allowPastDates}
              mode="single"
              month={month}
              onMonthChange={setMonth}
              selected={selectedDate}
              onSelect={(date) => {
                if (!date) return;
                if (minimumDate && date < minimumDate) return;
                if (maximumDate && date > maximumDate) return;
                onChange?.(toDateKey(date));
                setOpen(false);
              }}
              disabled={disabledDates}
              modifiers={getEventModifiers(eventStartDate, eventEndDate)}
              modifiersClassNames={eventModifierClassNames}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {!required && value && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => onChange?.('')}
            aria-label="Clear date"
            title="Clear date"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <EventContextHint
        eventStartDate={eventStartDate}
        eventEndDate={eventEndDate}
        selectedStart={value}
        helperText={helperText}
      />
      {validationMessage && <p className="text-xs text-destructive">{validationMessage}</p>}
    </div>
  );
}

export function EventAwareDateRangePicker({
  id,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  eventStartDate,
  eventEndDate,
  label = 'Date range',
  startLabel = 'Start',
  endLabel = 'End',
  required = false,
  disabled = false,
  defaultToEventRange = true,
  minimumNights = 0,
  minDate,
  maxDate,
  allowPastDates = false,
  helperText,
  validationMessage,
  className,
  triggerClassName,
  'data-testid': dataTestId,
}) {
  const [open, setOpen] = React.useState(false);
  const eventStartKey = getDateKey(eventStartDate);
  const eventEndKey = getDateKey(eventEndDate) || eventStartKey;
  const minimumDateKey = getMinimumDateKey(minDate, allowPastDates);
  const defaultStartKey = clampToMinimumKey(eventStartKey, minimumDateKey);
  const eventDefaultEndKey = clampToMinimumKey(eventEndKey, minimumDateKey);
  const earliestDefaultEndKey = addDaysToKey(defaultStartKey, Math.max(minimumNights, 0));
  const defaultEndKey = eventDefaultEndKey && eventDefaultEndKey >= earliestDefaultEndKey
    ? eventDefaultEndKey
    : earliestDefaultEndKey;
  const startDate = fromDateKey(startValue);
  const endDate = fromDateKey(endValue);
  const invalidRange = isEndBeforeStart(startValue, endValue);
  const selectedEndDate = endDate || (minimumNights === 0 ? startDate : undefined);
  const selectedRange = startDate && !invalidRange ? { from: startDate, to: selectedEndDate } : undefined;
  const minimumDate = fromDateKey(minimumDateKey);
  const maximumDate = fromDateKey(maxDate);
  const disabledDates = [
    minimumDate ? { before: minimumDate } : null,
    maximumDate ? { after: maximumDate } : null,
  ].filter(Boolean);
  const [month, setMonth] = React.useState(
    fromDateKey(clampToMinimumKey(
      getDateKey(startValue) || defaultStartKey || minimumDateKey,
      minimumDateKey
    )) || minimumDate
  );

  React.useEffect(() => {
    if (!defaultToEventRange || !defaultStartKey) return;
    if (!startValue) onStartChange?.(defaultStartKey);
    if (!endValue && defaultEndKey) onEndChange?.(defaultEndKey);
  }, [defaultEndKey, defaultStartKey, defaultToEventRange, endValue, onEndChange, onStartChange, startValue]);

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      if (defaultToEventRange && defaultStartKey) {
        if (!startValue) onStartChange?.(defaultStartKey);
        if (!endValue && defaultEndKey) onEndChange?.(defaultEndKey);
      }
      setMonth(fromDateKey(clampToMinimumKey(
        getDateKey(startValue) || defaultStartKey || minimumDateKey,
        minimumDateKey
      )) || minimumDate);
    }
    setOpen(nextOpen);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && <p className="text-sm font-medium">{label}{required ? ' *' : ''}</p>}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            aria-required={required}
            aria-invalid={invalidRange || Boolean(validationMessage)}
            className={cn('h-auto min-h-10 w-full justify-start rounded-md px-3 py-2 text-left font-normal', !startValue && !endValue && 'text-muted-foreground', triggerClassName)}
            data-testid={dataTestId}
          >
            <CalendarDays className="mr-2 h-4 w-4" />
            <span className="min-w-0">
              <span className="block text-xs text-muted-foreground">{startLabel} - {endLabel}</span>
              <span className="block truncate">
                {formatDateRangeLabel(startValue, endValue)}
              </span>
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" collisionPadding={12} className="z-[90] w-[19rem] max-w-[calc(100vw-1.5rem)] p-0">
          <Calendar
            allowPastDates={allowPastDates}
            mode="range"
            month={month}
            onMonthChange={setMonth}
            selected={selectedRange}
            onSelect={(range) => {
              const nextStart = range?.from ? toDateKey(range.from) : '';
              const nextEnd = range?.to ? toDateKey(range.to) : '';
              if (minimumDateKey && nextStart && nextStart < minimumDateKey) return;
              if (minimumDateKey && nextEnd && nextEnd < minimumDateKey) return;
              if (maximumDate && range?.from && range.from > maximumDate) return;
              if (maximumDate && range?.to && range.to > maximumDate) return;
              if (isEndBeforeStart(nextStart, nextEnd)) return;
              onStartChange?.(nextStart);
              onEndChange?.(nextEnd);
            }}
            min={minimumNights}
            disabled={disabledDates}
            modifiers={getEventModifiers(eventStartDate, eventEndDate)}
            modifiersClassNames={eventModifierClassNames}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {!required && (startValue || endValue) && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => {
            onStartChange?.('');
            onEndChange?.('');
          }}
        >
          <X className="h-3.5 w-3.5" />
          Clear range
        </Button>
      )}
      <EventContextHint
        eventStartDate={eventStartDate}
        eventEndDate={eventEndDate}
        selectedStart={startValue}
        selectedEnd={endValue}
        helperText={helperText}
      />
      {(invalidRange || validationMessage) && (
        <p className="text-xs text-destructive">
          {validationMessage || `${endLabel} cannot be earlier than ${startLabel}.`}
        </p>
      )}
    </div>
  );
}

export default EventAwareDatePicker;
